/**
 * AGC: Represents the subset of the EIP-1193 provider API required for listener cleanup.
 * AGC: Some shims (Farcaster Base App) omit `removeAllListeners`, so all properties are optional.
 */
export interface EthereumListenerHost {
  removeAllListeners?: (eventName?: string) => void
  removeListener?: (eventName: string, handler: ((...args: unknown[]) => void | Promise<void>)) => void
}

/**
 * AGC: Shared type for Ethereum event handlers so async handlers remain type-safe.
 */
export type EthereumEventHandler = (...args: unknown[]) => void | Promise<void>

/**
 * AGC: Safely detaches Ethereum event listeners, preferring `removeAllListeners` when available.
 * AGC: Falls back to `removeListener` so Base App’s lightweight providers cannot crash cleanup logic.
 */
export const detachEthereumListeners = (
  ethereum: EthereumListenerHost | undefined,
  listeners: Record<string, EthereumEventHandler | undefined>
): void => {
  if (!ethereum) {
    // AGC: Nothing to clean up if the provider never initialized (desktop-only path, tests, etc.).
    return
  }

  Object.entries(listeners).forEach(([eventName, handler]) => {
    if (!handler) {
      // AGC: Skip undefined handlers so we never call removeListener with an invalid reference.
      return
    }

    if (typeof ethereum.removeAllListeners === 'function') {
      // AGC: Preferred path – removes every listener for this event with one call.
      ethereum.removeAllListeners(eventName)
      return
    }

    if (typeof ethereum.removeListener === 'function') {
      // AGC: Base App shim path – remove the specific handler reference we registered earlier.
      ethereum.removeListener(eventName, handler)
      return
    }

    // AGC: No removal API is present; log once per event for debugging without throwing.
    console.warn(`AGC: Unable to remove ${eventName} listener because provider lacks removal APIs.`)
  })
}

